import React, { useState } from "react";
import "@/app/pages/companyProfile/addedBy/MediaContainer.scss";
import { CompanyEnhancedFields } from "@/app/pages/companyProfile/addedBy/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faCircleXmark,
  faCirclePlay,
} from "@fortawesome/free-solid-svg-icons";

interface MediaOverlayProps {
  media: Array<{ url: string; type: string }>;
  currentIndex: number;
  onClose: () => void;
}

const MediaOverlay: React.FC<MediaOverlayProps> = ({
  media,
  currentIndex,
  onClose,
}) => {
  const [current, setCurrent] = useState(currentIndex);

  const handlePrevious = () => {
    setCurrent((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  };

  const handleNext = () => {
    setCurrent((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  };

  const currentMedia = media[current];

  return (
    <div className="media-overlay">
      <div className="media-overlay-content">
        {currentMedia.type === "image" ? (
          <img src={currentMedia.url} alt={`Media ${current}`} />
        ) : currentMedia.type === "video" ? (
          <iframe
            width="100%"
            height="100%"
            src={currentMedia.url}
            title={`Video ${current}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        ) : null}
        <button
          className="media-overlay-arrow media-overlay-arrow-left"
          onClick={handlePrevious}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ color: "#686e78" }} size="xl" />
        </button>
        <button
          className="media-overlay-arrow media-overlay-arrow-right"
          onClick={handleNext}
        >
          <FontAwesomeIcon icon={faChevronRight} style={{ color: "#686e78" }} size="xl" />
        </button>
        <button className="media-overlay-close-wrapper" onClick={onClose}>
          <span className="media-overlay-close-text">Close</span>
          <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#f5f5f5" }} size="xs" />
        </button>
      </div>
    </div>
  );
};

interface MediaCardContainerProps {
  companyEnhancedFields: CompanyEnhancedFields;
}

const MediaCardContainer: React.FC<MediaCardContainerProps> = ({
  companyEnhancedFields,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayIndex, setOverlayIndex] = useState(0);
  const [inputValue, setInputValue] = useState("1");
  const itemsPerPage = 6;

  const media = [
    ...(companyEnhancedFields.galleryPhotoUrls || [])
      .filter((url) => url) // Filter out any null or empty URLs
      .map((url) => ({ url, type: "image" })),
    ...(companyEnhancedFields.videoUrls || [])
      .filter((url) => url) // Filter out any null or empty URLs
      .map((url) => ({ url, type: "video" })),
  ];

  const totalPages = Math.ceil(media.length / itemsPerPage);

  const handlePageChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value === "") {
      return;
    }

    const page = parseInt(value, 10);
    if (!isNaN(page) && page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setInputValue((currentPage + 1).toString());
    }
  };

  const handlePagePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setInputValue((currentPage - 1).toString());
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, media.length);
  const currentMedia = media.slice(startIndex, endIndex);

  const openOverlay = (index) => {
    setOverlayIndex(index);
    setShowOverlay(true);
  };

  const closeOverlay = () => {
    setShowOverlay(false);
  };

  return (
    <>
      <div className="media-grid">
        {currentMedia.map((item, index) => (
          <div
            key={index}
            className="media-item"
            onClick={() => openOverlay(startIndex + index)}
          >
            {item.type === "image" ? (
              <img src={item.url} alt={`Media ${index}`} />
            ) : item.type === "video" ? (
              <div className="video-placeholder">
                <FontAwesomeIcon icon={faCirclePlay} style={{ color: "#ffffff" }} />
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <div className="pagination">
        <p className="paraText">Page</p>
        <input
          type="text"
          value={inputValue}
          onChange={handlePageChange}
          placeholder="Page"
        />
        <p className="paraText">Of</p>
        <p className="paraText">{totalPages}</p>
        <button
          className="pagination-button"
          onClick={handlePagePrevious}
          disabled={currentPage === 1}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ color: "#686e78" }} />
        </button>
        <button
          className="pagination-button"
          onClick={handlePageNext}
          disabled={currentPage === totalPages}
        >
          <FontAwesomeIcon icon={faChevronRight} style={{ color: "#686e78" }} />
        </button>
      </div>
      <div className="pagination">
        <p>
          Clicking on a video link will redirect you to a third party website not governed by the Dun & Bradstreet Privacy Policy. Dun & Bradstreet disclaims any liability for information made available on or through these links.
        </p>
      </div>

      {showOverlay && (
        <MediaOverlay
          media={media}
          currentIndex={overlayIndex}
          onClose={closeOverlay}
        />
      )}
    </>
  );
};

export default MediaCardContainer;
